import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Edit2, Trash2, ZoomIn, ZoomOut, Maximize2, CheckCircle, XCircle } from 'lucide-react';

// Default tags if none provided
const DEFAULT_TAGS = [
  { tag: 'person', color: '#10b981' },
  { tag: 'vehicle', color: '#3b82f6' },
  { tag: 'animal', color: '#f59e0b' },
  { tag: 'object', color: '#8b5cf6' },
  { tag: 'building', color: '#ef4444' },
  { tag: 'text', color: '#06b6d4' },
  { tag: 'other', color: '#6b7280' }
];

const ZOOM_FACTOR = 1.2;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const MIN_BOX_SIZE = 5;
const HANDLE_SIZE = 10;
const LINE_WIDTH = 5;
const LABEL_FONT_SIZE = 20;
const MIN_COMMENT_LENGTH = 5;

// Confirmation Dialog Component
const ConfirmDialog = ({ show, action, hasAnnotations, onConfirm, onCancel }) => {
  if (!show) return null;

  const isAccept = action === 'accept';
  const title = isAccept ? 'Accept Image' : 'Reject Image';
  const message = isAccept
    ? 'Are you sure you want to accept this image?'
    : 'Are you sure you want to reject this image?';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          {isAccept ? (
            <CheckCircle className="w-6 h-6 text-green-600" />
          ) : (
            <XCircle className="w-6 h-6 text-red-600" />
          )}
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        
        <p className="text-gray-600 mb-6">{message}</p>
        
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 text-white rounded-md ${
              isAccept
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isAccept ? 'Accept (E)' : 'Reject (E)'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Annotation Dialog Component
const AnnotationDialog = ({ show, position, onClose, onSave, editingId, initialTag, initialComment, tags }) => {
  // A new box starts on the first tag rather than "Choose a tag...", so the
  // common case is draw-then-confirm with no dropdown interaction. Editing an
  // existing annotation keeps whatever tag it already had.
  const defaultTag = editingId ? (initialTag || '') : (initialTag || tags[0]?.tag || '');
  const [selectedTag, setSelectedTag] = useState(defaultTag);
  const [comment, setComment] = useState(initialComment || '');
  const dialogRef = useRef(null);

  useEffect(() => {
    setSelectedTag(editingId ? (initialTag || '') : (initialTag || tags[0]?.tag || ''));
    setComment(initialComment || '');
  }, [initialTag, initialComment, editingId, tags]);

  useEffect(() => {
    if (show && dialogRef.current) {
      const selectElement = dialogRef.current.querySelector('select');
      if (selectElement) selectElement.focus();
    }
  }, [show]);

  // Held in a ref so the key handler always calls the current save without
  // being rebound on every keystroke in the comment box.
  const saveRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      const typing = event.target && (event.target.tagName === 'TEXTAREA'
                                      || event.target.tagName === 'INPUT');
      const isConfirm = (event.key === 'e' || event.key === 'E') && !typing
                        && !event.ctrlKey && !event.metaKey && !event.altKey;
      const isEnter = event.key === 'Enter' && (event.ctrlKey || event.metaKey);
      if (!isConfirm && !isEnter) return;
      // preventDefault matters for the select: 'e' would otherwise jump the
      // dropdown to an option beginning with that letter.
      event.preventDefault();
      saveRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onClose]);

  const handleSave = () => {
    if (selectedTag) {
      onSave(selectedTag, comment.trim());
      setSelectedTag('');
      setComment('');
    }
  };
  saveRef.current = handleSave;

  if (!show) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && e.ctrlKey && selectedTag) {
      handleSave();
    }
  };

  // For edit mode, use centered modal
  if (editingId) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Edit Annotation</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Tag *
              </label>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a tag...</option>
                {tags.map(({ tag, color }) => (
                  <option key={tag} value={tag}>
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment (optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add any additional notes..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedTag}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For new annotations, show popover at position
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div 
        ref={dialogRef}
        className="fixed z-50 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-80"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: position.transform
        }}
        onKeyDown={handleKeyDown}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Add Annotation</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Tag *
            </label>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              <option value="">Choose a tag...</option>
              {tags.map(({ tag, color }) => (
                <option key={tag} value={tag}>
                  {tag.charAt(0).toUpperCase() + tag.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Comment (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add notes..."
              rows={2}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel (Esc)
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedTag}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save (E)
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Annotation List Item Component
const AnnotationItem = ({ annotation, index, isSelected, onSelect, onEdit, onDelete, tags }) => {
  const tagConfig = tags.find(t => t.tag === annotation.tag);
  const color = tagConfig?.color || '#6b7280';
  
  return (
    <div
      className={`p-3 rounded border-2 cursor-pointer transition-colors ${
        isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 bg-gray-50 hover:border-blue-300'
      }`}
      onClick={onSelect}
      style={isSelected ? { borderColor: color, backgroundColor: color + '10' } : {}}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">
            #{index + 1}
          </span>
          <span 
            className="px-2 py-1 text-white text-xs font-medium rounded"
            style={{ backgroundColor: color }}
          >
            {annotation.tag.charAt(0).toUpperCase() + annotation.tag.slice(1)}
          </span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(annotation);
            }}
            className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded"
          >
            <Edit2 className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(annotation.id);
            }}
            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="text-xs text-gray-600 space-y-1">
        <div>Size: {annotation.bbox.width.toFixed(1)}% × {annotation.bbox.height.toFixed(1)}%</div>
        <div>Pos: ({annotation.bbox.x.toFixed(1)}%, {annotation.bbox.y.toFixed(1)}%)</div>
      </div>
      {annotation.comment && (
        <p className="mt-2 text-xs text-gray-700 border-t pt-2">{annotation.comment}</p>
      )}
    </div>
  );
};

// Main Component
export default function BBoxAnnotator({ 
  imageUrl, 
  review_lock_id, 
  review_id, 
  onComplete, 
  onAnnotationsChange,
  containerMode = false,
  tags = DEFAULT_TAGS,
  initialAnnotations = []
}) {
  const [annotations, setAnnotations] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentBox, setCurrentBox] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingAnnotation, setPendingAnnotation] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [editingId, setEditingId] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [selectedAnnId, setSelectedAnnId] = useState(null);
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0, transform: '' });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [generalComment, setGeneralComment] = useState('');
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Load initial annotations when they change
  useEffect(() => {
    if (initialAnnotations && initialAnnotations.length > 0) {
      console.log('Loading initial annotations:', initialAnnotations);
      setAnnotations(initialAnnotations);
    }
  }, [initialAnnotations]);

  // Notify parent of annotation changes
  useEffect(() => {
    if (onAnnotationsChange) {
      onAnnotationsChange(annotations.length > 0);
    }
  }, [annotations.length, onAnnotationsChange]);

  // Create tag color lookup
  const tagColorMap = tags.reduce((acc, { tag, color }) => {
    acc[tag] = color;
    return acc;
  }, {});

  // Utility functions
  const pixelsToPercent = useCallback((bbox) => ({
    x: (bbox.x / imageDimensions.width) * 100,
    y: (bbox.y / imageDimensions.height) * 100,
    width: (bbox.width / imageDimensions.width) * 100,
    height: (bbox.height / imageDimensions.height) * 100
  }), [imageDimensions]);

  const percentToPixels = useCallback((bbox) => ({
    x: (bbox.x / 100) * imageDimensions.width,
    y: (bbox.y / 100) * imageDimensions.height,
    width: (bbox.width / 100) * imageDimensions.width,
    height: (bbox.height / 100) * imageDimensions.height
  }), [imageDimensions]);

  const getMousePos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom
    };
  }, [zoom]);

  const getResizeHandle = useCallback((pos, bbox) => {
    const bboxPx = percentToPixels(bbox);
    const handleSize = 12 / zoom;
    const corners = {
      nw: { x: bboxPx.x, y: bboxPx.y },
      ne: { x: bboxPx.x + bboxPx.width, y: bboxPx.y },
      sw: { x: bboxPx.x, y: bboxPx.y + bboxPx.height },
      se: { x: bboxPx.x + bboxPx.width, y: bboxPx.y + bboxPx.height }
    };

    for (const [handle, corner] of Object.entries(corners)) {
      if (Math.abs(pos.x - corner.x) < handleSize && Math.abs(pos.y - corner.y) < handleSize) {
        return handle;
      }
    }
    return null;
  }, [percentToPixels, zoom]);

  const isInsideBBox = useCallback((pos, bbox) => {
    const bboxPx = percentToPixels(bbox);
    return pos.x >= bboxPx.x && pos.x <= bboxPx.x + bboxPx.width &&
           pos.y >= bboxPx.y && pos.y <= bboxPx.y + bboxPx.height;
  }, [percentToPixels]);

  // Drawing function
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !imageDimensions.width) return;
    
    const ctx = canvas.getContext('2d');
    
    canvas.width = imageDimensions.width * zoom;
    canvas.height = imageDimensions.height * zoom;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    
    annotations.forEach(ann => {
      const bboxPx = percentToPixels(ann.bbox);
      const x = bboxPx.x * zoom;
      const y = bboxPx.y * zoom;
      const w = bboxPx.width * zoom;
      const h = bboxPx.height * zoom;
      
      const isSelected = ann.id === selectedAnnId;
      const color = tagColorMap[ann.tag] || '#6b7280';
      
      ctx.strokeStyle = color;
      ctx.lineWidth = (isSelected ? LINE_WIDTH + 1 : LINE_WIDTH) * zoom;
      ctx.strokeRect(x, y, w, h);
      
      if (isSelected) {
        ctx.shadowColor = color;
        ctx.shadowBlur = 5 * zoom;
        ctx.strokeRect(x, y, w, h);
        ctx.shadowBlur = 0;
      }
      
      const labelText = ann.tag.charAt(0).toUpperCase() + ann.tag.slice(1);
      const fontSize = LABEL_FONT_SIZE * zoom;
      ctx.font = `bold ${fontSize}px sans-serif`;
      const textMetrics = ctx.measureText(labelText);
      const labelPadding = 6 * zoom;
      const labelHeight = fontSize + labelPadding;
      const labelWidth = textMetrics.width + labelPadding * 2;
      
      ctx.fillStyle = color;
      ctx.fillRect(x, y - labelHeight - 4 * zoom, labelWidth, labelHeight);
      
      ctx.fillStyle = '#ffffff';
      ctx.fillText(labelText, x + labelPadding, y - labelPadding - 4 * zoom);

      const handleSize = HANDLE_SIZE * zoom;
      
      [
        [x, y],
        [x + w, y],
        [x, y + h],
        [x + w, y + h]
      ].forEach(([px, py]) => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(px - handleSize/2, py - handleSize/2, handleSize, handleSize);
        
        ctx.strokeStyle = color;
        ctx.lineWidth = 2 * zoom;
        ctx.strokeRect(px - handleSize/2, py - handleSize/2, handleSize, handleSize);
        
        if (isSelected) {
          ctx.fillStyle = color;
          ctx.fillRect(px - handleSize/4, py - handleSize/4, handleSize/2, handleSize/2);
        }
      });
    });
    
    if (currentBox) {
      const x = currentBox.startX * zoom;
      const y = currentBox.startY * zoom;
      const width = (currentBox.endX - currentBox.startX) * zoom;
      const height = (currentBox.endY - currentBox.startY) * zoom;
      
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = LINE_WIDTH * zoom;
      ctx.setLineDash([8 * zoom, 4 * zoom]);
      ctx.strokeRect(x, y, width, height);
      ctx.setLineDash([]);
      
      const markerSize = 12 * zoom;
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 3 * zoom;
      
      ctx.beginPath();
      ctx.moveTo(x, y + markerSize);
      ctx.lineTo(x, y);
      ctx.lineTo(x + markerSize, y);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(x + width - markerSize, y);
      ctx.lineTo(x + width, y);
      ctx.lineTo(x + width, y + markerSize);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(x, y + height - markerSize);
      ctx.lineTo(x, y + height);
      ctx.lineTo(x + markerSize, y + height);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(x + width - markerSize, y + height);
      ctx.lineTo(x + width, y + height);
      ctx.lineTo(x + width, y + height - markerSize);
      ctx.stroke();
    }
  }, [annotations, currentBox, imageDimensions, percentToPixels, selectedAnnId, zoom, tagColorMap]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const handleImageLoad = () => {
    if (imageRef.current && containerRef.current) {
      const img = imageRef.current;
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      
      const container = containerRef.current;
      const scaleX = container.clientWidth / img.naturalWidth;
      const scaleY = container.clientHeight / img.naturalHeight;
      const initialZoom = Math.min(scaleX, scaleY, 1) * 0.9;
      
      setZoom(initialZoom);
    }
  };

  const handleAccept = useCallback(() => {
    setConfirmAction('accept');
    setShowConfirmDialog(true);
  }, []);

  const handleReject = useCallback(() => {
    setConfirmAction('reject');
    setShowConfirmDialog(true);
  }, []);

  const handleConfirm = useCallback(() => {
    console.log("=".repeat(80));
    console.log("BBOX ANNOTATOR - PREPARING RESULT");
    console.log("=".repeat(80));
    
    console.log("Raw annotations array:", annotations);
    console.log("Number of annotations:", annotations.length);
    
    annotations.forEach((ann, idx) => {
      console.log(`\nAnnotation ${idx + 1}:`);
      console.log("  ID:", ann.id);
      console.log("  Tag:", ann.tag);
      console.log("  Comment:", ann.comment);
      console.log("  BBox (raw):", ann.bbox);
      console.log("  BBox keys:", Object.keys(ann.bbox));
    });
    
    const result = {
      status: confirmAction === 'accept' ? 'accepted' : 'rejected',
      review_lock_id,
      review_id,
      comment: generalComment.trim() || undefined,
      labels_data: {
        annotations: annotations.map(ann => ({
          id: ann.id,
          bbox: {
            x: parseFloat(ann.bbox.x.toFixed(2)),
            y: parseFloat(ann.bbox.y.toFixed(2)),
            width: parseFloat(ann.bbox.width.toFixed(2)),
            height: parseFloat(ann.bbox.height.toFixed(2))
          },
          tag: ann.tag,
          comment: ann.comment
        }))
      }
    };
    
    console.log("\nFinal result object:");
    console.log(JSON.stringify(result, null, 2));
    console.log("=".repeat(80));
    
    setShowConfirmDialog(false);
    if (onComplete) {
      onComplete(result);
    } else {
      console.log('Review result:', result);
    }
  }, [confirmAction, review_lock_id, review_id, generalComment, annotations, onComplete]);

  // Validation logic
  const canAccept = annotations.length === 0;
  const canReject = annotations.length > 0 || generalComment.trim().length >= MIN_COMMENT_LENGTH;

  // Keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in textarea or input
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
        return;
      }

      // The annotation dialog owns the keyboard while it is open: 'e' confirms
      // the tag there, and 'q' must not accept/reject the whole image from
      // underneath it.
      if (showDialog) {
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        setIsSpacePressed(true);
      }
      // Q key - Trigger the active action (Accept or Reject)
      else if (e.key === 'q' || e.key === 'Q') {
        e.preventDefault();
        // If confirmation dialog is open, do nothing (let 'e' handle confirmation)
        if (showConfirmDialog) return;
        
        // Trigger the active action
        if (canAccept) {
          handleAccept();
        } else if (canReject) {
          handleReject();
        }
      }
      // E key - Confirm action in dialog
      else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        // If dialog is open, confirm the action
        if (showConfirmDialog) {
          handleConfirm();
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsSpacePressed(false);
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [showConfirmDialog, showDialog, canAccept, canReject, handleAccept, handleReject, handleConfirm]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const delta = -e.deltaY;
    const zoomFactor = delta > 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;
    const newZoom = Math.max(MIN_ZOOM, Math.min(zoom * zoomFactor, MAX_ZOOM));
    
    if (newZoom !== zoom) {
      setZoom(newZoom);
    }
  }, [zoom]);
  
  const handleMouseDown = (e) => {
    if (!imageUrl) return;
    
    if (e.button === 1 || (e.button === 0 && isSpacePressed)) {
      e.preventDefault();
      const canvas = canvasRef.current;
      const currentTransform = canvas.style.transform || 'translate(0px, 0px)';
      const matches = currentTransform.match(/translate\((-?\d+(?:\.\d+)?)px,\s*(-?\d+(?:\.\d+)?)px\)/);
      const currentX = matches ? parseFloat(matches[1]) : 0;
      const currentY = matches ? parseFloat(matches[2]) : 0;
      
      setIsPanning(true);
      setPanStart({ x: e.clientX - currentX, y: e.clientY - currentY });
      return;
    }
    
    if (e.button !== 0 || isSpacePressed) return;
    
    const pos = getMousePos(e);

    for (const ann of [...annotations].reverse()) {
      const handle = getResizeHandle(pos, ann.bbox);
      if (handle) {
        setResizing({ id: ann.id, handle, startPos: pos, originalBbox: { ...ann.bbox } });
        return;
      }
      
      if (isInsideBBox(pos, ann.bbox)) {
        const bboxPx = percentToPixels(ann.bbox);
        setDragging({ 
          id: ann.id, 
          offsetX: pos.x - bboxPx.x, 
          offsetY: pos.y - bboxPx.y,
          originalBbox: { ...ann.bbox }
        });
        return;
      }
    }

    setIsDrawing(true);
    setCurrentBox({ startX: pos.x, startY: pos.y, endX: pos.x, endY: pos.y });
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      const canvas = canvasRef.current;
      if (canvas) {
        const dx = e.clientX - panStart.x;
        const dy = e.clientY - panStart.y;
        canvas.style.transform = `translate(${dx}px, ${dy}px)`;
      }
      return;
    }
    
    const pos = getMousePos(e);

    if (resizing) {
      const ann = annotations.find(a => a.id === resizing.id);
      if (!ann) return;

      const originalPx = percentToPixels(resizing.originalBbox);
      const dx = pos.x - resizing.startPos.x;
      const dy = pos.y - resizing.startPos.y;

      let newBbox = { ...originalPx };

      switch (resizing.handle) {
        case 'nw':
          newBbox.x = originalPx.x + dx;
          newBbox.y = originalPx.y + dy;
          newBbox.width = originalPx.width - dx;
          newBbox.height = originalPx.height - dy;
          break;
        case 'ne':
          newBbox.y = originalPx.y + dy;
          newBbox.width = originalPx.width + dx;
          newBbox.height = originalPx.height - dy;
          break;
        case 'sw':
          newBbox.x = originalPx.x + dx;
          newBbox.width = originalPx.width - dx;
          newBbox.height = originalPx.height + dy;
          break;
        case 'se':
        default:
          newBbox.width = originalPx.width + dx;
          newBbox.height = originalPx.height + dy;
          break;
      }

      if (newBbox.width > 10 && newBbox.height > 10) {
        setAnnotations(annotations.map(a => 
          a.id === resizing.id ? { ...a, bbox: pixelsToPercent(newBbox) } : a
        ));
      }
      return;
    }

    if (dragging) {
      const ann = annotations.find(a => a.id === dragging.id);
      if (!ann) return;

      const originalPx = percentToPixels(dragging.originalBbox);
      const newX = pos.x - dragging.offsetX;
      const newY = pos.y - dragging.offsetY;

      const clampedX = Math.max(0, Math.min(newX, imageDimensions.width - originalPx.width));
      const clampedY = Math.max(0, Math.min(newY, imageDimensions.height - originalPx.height));

      setAnnotations(annotations.map(a => 
        a.id === dragging.id ? { 
          ...a, 
          bbox: pixelsToPercent({ 
            x: clampedX, 
            y: clampedY, 
            width: originalPx.width, 
            height: originalPx.height 
          }) 
        } : a
      ));
      return;
    }

    if (isDrawing) {
      setCurrentBox(prev => ({ ...prev, endX: pos.x, endY: pos.y }));
    }
  };

  const handleMouseUp = (e) => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (resizing) {
      setResizing(null);
      return;
    }

    if (dragging) {
      setDragging(null);
      return;
    }

    if (!isDrawing || !currentBox) return;
    
    const width = Math.abs(currentBox.endX - currentBox.startX);
    const height = Math.abs(currentBox.endY - currentBox.startY);
    
    if (width > MIN_BOX_SIZE && height > MIN_BOX_SIZE) {
      const bboxPx = {
        x: Math.min(currentBox.startX, currentBox.endX),
        y: Math.min(currentBox.startY, currentBox.endY),
        width,
        height
      };
      
      setPendingAnnotation({ 
        id: `ann_${Date.now()}`, 
        bbox: pixelsToPercent(bboxPx)
      });
      
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      const bboxScreenX = rect.left + (bboxPx.x * zoom);
      const bboxScreenY = rect.top + (bboxPx.y * zoom);
      const bboxScreenBottom = rect.top + ((bboxPx.y + bboxPx.height) * zoom);
      const bboxScreenRight = rect.left + ((bboxPx.x + bboxPx.width) * zoom);
      
      const dialogWidth = 320;
      const dialogHeight = 280;
      
      const mouseMovedDown = currentBox.endY > currentBox.startY;
      
      const spaceAbove = bboxScreenY - containerRect.top;
      const spaceBelow = containerRect.bottom - bboxScreenBottom;
      
      let posX, posY, transform = '';
      
      if (bboxScreenX + dialogWidth <= window.innerWidth) {
        posX = bboxScreenX;
      } else if (bboxScreenRight - dialogWidth >= 0) {
        posX = bboxScreenRight - dialogWidth;
      } else {
        posX = bboxScreenX + (bboxPx.width * zoom) / 2;
        transform = 'translateX(-50%)';
      }
      
      if (mouseMovedDown) {
        if (spaceBelow >= dialogHeight + 20) {
          posY = bboxScreenBottom + 10;
        } else if (spaceAbove >= dialogHeight + 20) {
          posY = bboxScreenY - dialogHeight - 10;
        } else {
          posY = Math.max(containerRect.top + 10, bboxScreenY - dialogHeight - 10);
        }
      } else {
        if (spaceAbove >= dialogHeight + 20) {
          posY = bboxScreenY - dialogHeight - 10;
        } else if (spaceBelow >= dialogHeight + 20) {
          posY = bboxScreenBottom + 10;
        } else {
          posY = Math.min(containerRect.bottom - dialogHeight - 10, bboxScreenBottom + 10);
        }
      }
      
      posX = Math.max(10, Math.min(posX, window.innerWidth - dialogWidth - 10));
      posY = Math.max(10, Math.min(posY, window.innerHeight - dialogHeight - 10));
      
      setDialogPosition({ x: posX, y: posY, transform });
      setShowDialog(true);
    }
    
    setIsDrawing(false);
    setCurrentBox(null);
  };

  const handleSaveAnnotation = (tag, comment) => {
    if (pendingAnnotation) {
      if (editingId) {
        setAnnotations(annotations.map(ann => 
          ann.id === editingId ? {
            ...ann,
            tag,
            comment
          } : ann
        ));
        setEditingId(null);
      } else {
        setAnnotations([...annotations, {
          ...pendingAnnotation,
          tag,
          comment
        }]);
      }
      
      setShowDialog(false);
      setPendingAnnotation(null);
      setEditingAnnotation(null);
    }
  };

  const handleCancelAnnotation = () => {
    setShowDialog(false);
    setPendingAnnotation(null);
    setEditingId(null);
    setEditingAnnotation(null);
  };

  const handleEditAnnotation = (ann) => {
    setPendingAnnotation({ id: ann.id, bbox: ann.bbox });
    setEditingAnnotation(ann);
    setEditingId(ann.id);
    setShowDialog(true);
  };

  const handleDeleteAnnotation = (id) => {
    setAnnotations(annotations.filter(ann => ann.id !== id));
    if (selectedAnnId === id) setSelectedAnnId(null);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev * ZOOM_FACTOR, MAX_ZOOM));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / ZOOM_FACTOR, MIN_ZOOM));
  
  const handleZoomFit = () => {
    if (imageRef.current && containerRef.current) {
      const img = imageRef.current;
      const container = containerRef.current;
      const scaleX = container.clientWidth / img.naturalWidth;
      const scaleY = container.clientHeight / img.naturalHeight;
      setZoom(Math.min(scaleX, scaleY, 1) * 0.9);
    }
  };

  return (
    <div className={`${containerMode ? 'h-full' : 'h-screen'} bg-gray-50 flex flex-col overflow-hidden`}>
      {/* Header with Available Tags */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">Image Annotation</h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Available tags:</span>
            <div className="flex gap-2">
              {tags.map(({ tag, color }) => (
                <span
                  key={tag}
                  className="px-2 py-1 text-xs font-medium text-white rounded"
                  style={{ backgroundColor: color }}
                >
                  {tag.charAt(0).toUpperCase() + tag.slice(1)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Annotation Interface */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 flex flex-col bg-white border-r overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-200 rounded"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium min-w-16 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-200 rounded"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={handleZoomFit}
                className="p-2 hover:bg-gray-200 rounded"
                title="Fit to Screen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
            <span className="text-sm text-gray-600">
              Click & drag to draw • Drag box to move • Corners to resize • Space to pan
            </span>
          </div>

          <div ref={containerRef} className="flex-1 bg-gray-100 relative overflow-hidden">
            <div className="absolute inset-0 overflow-auto">
              <div className="inline-block min-w-full min-h-full flex items-center justify-center">
                <div className="relative">
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="Review"
                    style={{ display: 'none' }}
                    onLoad={handleImageLoad}
                    crossOrigin="anonymous"
                  />
                  <canvas
                    ref={canvasRef}
                    className={isPanning || isSpacePressed ? 'cursor-grab' : 'cursor-crosshair'}
                    style={{ cursor: isPanning ? 'grabbing' : (isSpacePressed ? 'grab' : 'crosshair') }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onWheel={handleWheel}
                    onMouseLeave={() => {
                      if (isDrawing) {
                        setIsDrawing(false);
                        setCurrentBox(null);
                      }
                      if (dragging) setDragging(null);
                      if (resizing) setResizing(null);
                      if (isPanning) setIsPanning(false);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-white flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-900">
              Annotations ({annotations.length})
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {annotations.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <p className="text-sm">No annotations yet</p>
                <p className="text-xs mt-1">Draw a box to mark issues</p>
              </div>
            ) : (
              annotations.map((ann, idx) => (
                <AnnotationItem
                  key={ann.id}
                  annotation={ann}
                  index={idx}
                  isSelected={selectedAnnId === ann.id}
                  onSelect={() => setSelectedAnnId(ann.id)}
                  onEdit={handleEditAnnotation}
                  onDelete={handleDeleteAnnotation}
                  tags={tags}
                />
              ))
            )}
          </div>

          <div className="p-4 border-t bg-gray-50 space-y-3">
            {/* General Comment Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                General Comment (optional)
              </label>
              <textarea
                value={generalComment}
                onChange={(e) => setGeneralComment(e.target.value)}
                placeholder="Add general notes about the image..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {generalComment.trim().length > 0 && generalComment.trim().length < MIN_COMMENT_LENGTH && (
                <p className="text-xs text-amber-600 mt-1">
                  At least {MIN_COMMENT_LENGTH} characters needed ({generalComment.trim().length}/{MIN_COMMENT_LENGTH})
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <button
              onClick={handleAccept}
              disabled={!canAccept}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Accept Image {canAccept && '(Q)'}
            </button>
            <button
              onClick={handleReject}
              disabled={!canReject}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <XCircle className="w-4 h-4" />
              Reject Image {canReject && '(Q)'}
            </button>
            {!canReject && (
              <p className="text-xs text-gray-500 text-center">
                To reject: add annotation(s) or write a comment ({MIN_COMMENT_LENGTH}+ chars)
              </p>
            )}
            {!canAccept && annotations.length > 0 && (
              <p className="text-xs text-amber-600 text-center">
                Remove all annotations to accept, or reject the image
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Annotation Dialog */}
      <AnnotationDialog
        show={showDialog}
        position={dialogPosition}
        onClose={handleCancelAnnotation}
        onSave={handleSaveAnnotation}
        editingId={editingId}
        initialTag={editingAnnotation?.tag}
        initialComment={editingAnnotation?.comment}
        tags={tags}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        show={showConfirmDialog}
        action={confirmAction}
        hasAnnotations={annotations.length}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
}